import axios from 'axios'
import User from './User.class'
import Simple from './SimpleName/Simple.class'
import moment from 'moment'
import {get} from 'lodash'
import Test from './Test.class'
import TourStateClass from './Tour/TourState.class'

const baseURL = '/api/tour/'

/**
 * @param {number | Object} init
 * @property {number} id
 * @property {string} name
 * @property {Simple} tournament
 * @property {TourStateClass} state
 * @property {Simple} type
 * @property {string} description
 * @property {string} mark_criteria
 * @property {User[]} juri
 * @property {Test[]} tests
 * @property {Date} start_at
 * @property {Date} finish_at
 * @property {Date} created_at
 * @property {Date} updated_at
 * @property {Date} deleted_at
 */
export default class TourClass {
	/**
	 * @constructor
	 * @param {number | Object} init
	 */
	constructor (init = {}) {
		this.id = null
		this.setDefaultData()

		if (typeof init === 'number') {
			this.id = init
			this.getSingle().then()
		} else {
			this.setDefaultData(init)
		}
	}

	setDefaultData (init = {}) {
		if (!init) return

		const dateFormat = 'YYYY-MM-DD HH:mm:ss'

		this.id = get(init, 'id') ? init.id : null
		this.name = get(init, 'name') ? init.name : ''
		this.start_at = get(init, 'start_at') && moment(init.start_at, dateFormat).isValid() ? moment(init.start_at, dateFormat) : null
		this.finish_at = get(init, 'finish_at') && moment(init.finish_at, dateFormat).isValid() ? moment(init.finish_at, dateFormat) : null
		this.created_at = get(init, 'created_at') && moment(init.created_at, dateFormat).isValid() ? moment(init.created_at, dateFormat) : null
		this.updated_at = get(init, 'updated_at') && moment(init.updated_at, dateFormat).isValid() ? moment(init.updated_at, dateFormat) : null
		this.deleted_at = get(init, 'deleted_at') && moment(init.deleted_at, dateFormat).isValid() ? moment(init.deleted_at, dateFormat) : null
		this.description = get(init, 'description') ? init.description : null
		this.mark_criteria = get(init, 'mark_criteria') ? init.mark_criteria : null

		this.tournament = get(init, 'tournament') ? new Simple(init.tournament) : new Simple()
		this.type = get(init, 'type') ? new Simple(init.type) : new Simple()
		this.state = get(init, 'state') ? new TourStateClass(init.state) : new TourStateClass()
		this.juri = get(init, 'juri') && Array.isArray(get(init.juri, 'data')) ? init.juri.data.map(el => new User(el))
			: Array.isArray(get(init, 'juri')) ? init.juri.map(el => new User(el)) : []
		this.tests = get(init, 'tests') && Array.isArray(get(init, 'tests')) ? init.tests.map(el => new Test(el))
			: get(init, 'tests') && Array.isArray(get(init.tests, 'data')) ? init.tests.data.map(el => new Test(el)) : []
		this.game_modes = get(init, 'game_modes') && Array.isArray(init.game_modes)
			? init.game_modes.map(el => new Simple(el))
			: []

		this.errors = {}
		this.isCreate = get(init, 'isCreate') ? init.isCreate : false
		this.mark_criteria_list = get(init, 'mark_criteria_list', []) || []
	}

	static url = baseURL
	/**
	 * get all tours
	 * @param {Object} params
	 * @returns {Promise<{data: TourClass[]}>}
	 */
	static getAll (params) {
		return axios.get(baseURL, {
			params
		})
			.then(res => {
				return {
					...res.data,
					data: this.createObjectFromArray(res.data.data)
				}
			})
			.catch(err => {
				console.error(err)
				return null
			})
	}
	static getSinglePromise (id) {
		return axios.get(`${baseURL}${id}`)
			.then(res => {
				return new TourClass(res.data.data)
			})
			.catch(err => {
				console.log(err)
				return null
			})
	}
	static createObjectFromArray (arr) {
		return arr.map(item => new TourClass(item))
	}
	/**
	 * get event by id
	 * @returns {Promise<void>}
	 */
	async getSingle () {
		try {
			const {data} = await axios.get(`${baseURL}${this.id}`)
			this.setDefaultData(data.data)
		} catch (e) {
			console.error(e)
		}
	}
	/**
	 * get form data
	 * @returns {FormData}
	 */
	get formData () {
		const formData = new FormData()

		// required field
		formData.append('name', this.name)

		if (this.tournament.id) {
			formData.append('tournament_id', this.tournament.id)
		}

		if (this.state.id) {
			formData.append('state_id', this.state.id)
		}

		if (this.type.id) {
			formData.append('type_id', this.type.id)
		}

		if (this.description) {
			formData.append('description', this.description)
		}

		// nullable field
		formData.append('start_at', this.start_at ? this.dateToServerFormat(this.start_at) : '')
		formData.append('finish_at', this.finish_at ? this.dateToServerFormat(this.finish_at) : '')

		if (this.mark_criteria) {
			formData.append('mark_criteria', this.mark_criteria)
		}

		if (this.mark_criteria_list) {
			this.mark_criteria_list.forEach((item) => {
				formData.append('mark_criteria_list[]', item)
			})
		}

		if (!this.isCreate) {
			if (
				Array.isArray(this.game_modes) &&
				this.game_modes.length > 0
			) {
				this.game_modes.forEach(a => {
					if (typeof a.id !== 'number') return
					formData.append('game_modes[]', String(a.id))
				})
			} else {
				formData.append('game_modes', '')
			}

			if (
				Array.isArray(this.juri) &&
				this.juri.length > 0
			) {
				this.juri.forEach(a => {
					if (typeof a.id !== 'number') return
					formData.append('juri[]', String(a.id))
				})
			} else {
				formData.append('juri', '')
			}
		}

		return formData
	}
	/**
	 * update tour by id
	 * @returns {Promise}
	 */
	update () {
		return axios.post(`${baseURL}${this.id}`, this.formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return err.response
			})
	}
	/**
	 * create tour by id
	 * @returns {Promise}
	 */
	async create () {
		return await axios.post(`${baseURL}`, this.formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return err.response
			})
	}
	/**
	 * delete tour by id
	 * @returns {boolean}
	 */
	async delete () {
		try {
			const res = await axios.delete(`${baseURL}${this.id}`)
			return res
		} catch (e) {
			console.error(e)
			return false
		}
	}
	static deleteById (id) {
		return axios.delete(`${baseURL}${id}`)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return false
			})
	}

	static restoreById (id) {
		return axios.post(`${baseURL}${id}/restore`)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return false
			})
	}

	restore () {
		return axios.post(`${baseURL}${this.id}/restore`)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return false
			})
	}

	dateToServerFormat (date) {
		return moment(date).format('YYYY-MM-DD HH:mm:ss')
	}

	static typeList = [
		{
			id: 1,
			name: 'Тест с автооценивание'
		},
		{
			id: 2,
			name: 'Тест с оценками жюри'
		},
		{
			id: 3,
			name: 'Интерактивная игра'
		},
		{
			id: 4,
			name: 'Оффлайн тур'
		}
	]

	get isTest () {
		return this.type.id === 1
	}

	get isJuri () {
		return this.type.id === 2
	}

	get isGame () {
		return this.type.id === 3
	}

	get isOffline () {
		return this.type.id === 4
	}

	/**
	 * @return {Date|null}
	 */
	getStartAt () {
		return this.start_at ? this.start_at.toDate() : null
	}

	/**
	 * @return {Date|null}
	 */
	getFinishAt () {
		return this.finish_at ? this.finish_at.toDate() : null
	}

	/**
	 * Преобразует входящие данные в moment объект
	 * @private
	 * @param {string|Date|moment.Moment|null} date
	 * @return {moment.Moment|null}
	 */
	dateToMoment (date = null) {
		let result = date

		const isString = typeof date === 'string'
		const isDate = date instanceof Date

		if (isString || isDate) {
			result = moment(date)
		} else if (moment.isMoment(date)) {
			result = date
		}

		return result
	}

	/**
	 * Установить дату начала тура
	 * @param {string|moment.Moment|null} startAt
	 */
	withStartAt (startAt = null) {
		const date = this.dateToMoment(startAt)

		this.start_at = date && date.isValid() ? date : null
	}

	/**
	 * Установить дату окончания тура
	 * @param {string|moment.Moment|null} startAt
	 */
	withFinishAt (startAt = null) {
		const date = this.dateToMoment(startAt)

		this.finish_at = date && date.isValid() ? date : null
	}

	createErrors (obj) {
		let newObj = {}
		for (let key in obj) {
			const newErrorStr = obj[key].reduce((accumulator, item) => {
				return accumulator + item
			}, '')

			newObj[key] = newErrorStr
		}
		this.errors = newObj
	}

	attachJuri (user) {
		const index = this.juri.findIndex(el => el.id === user.id)

		if (index === -1) {
			this.juri.push(user)
		} else {
			this.juri.splice(index, 1, user)
		}
	}

	detachJuri (user) {
		const index = this.juri.findIndex(el => el.id === user.id)

		if (index > -1) {
			this.juri.splice(index, 1)
		}
	}
}



// WEBPACK FOOTER //
// ./src/assets/js/Tour.class.js